import { pctFromBaseline } from './benchmarkEquitySeries';

/** YYYY-MM-DD in the trading timezone. Snapshots are an end-of-day print in this zone. */
export function tradingDateKey(now = new Date(), timeZone = 'America/New_York') {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

function roundMoney(value) {
  return parseFloat(Number(value).toFixed(2));
}

function sameMoney(a, b) {
  return Math.round(Number(a) * 100) === Math.round(Number(b) * 100);
}

function dateKey(value) {
  if (!value) return null;
  return String(value).slice(0, 10);
}

/**
 * Drawdown is equity minus the running peak. Earlier days keep the server
 * series; the latest point is recomputed from the live equity.
 */
function drawdownWithLiveEnd(curve, priorDrawdown) {
  const prior = new Map(
    (priorDrawdown || []).map((point) => [dateKey(point.time), point.drawdown])
  );
  let peak = 0;
  return curve.map((point, index) => {
    const equity = Number(point.equity) || 0;
    if (equity > peak) peak = equity;
    const time = dateKey(point.time);
    const isLast = index === curve.length - 1;
    const drawdown = isLast || !prior.has(time)
      ? roundMoney(equity - peak)
      : prior.get(time);
    return { time, drawdown };
  });
}

/**
 * Point the equity curve's latest value at the live account balance so period
 * P&L is not stuck on the prior end-of-day snapshot.
 *
 * When the last snapshot is already dated today, its equity is replaced.
 * When the last snapshot is older and the selected range still includes today,
 * a today point is appended.
 *
 * `rangeUntil` is the custom-range end (YYYY-MM-DD). Presets omit it and are
 * treated as open through today. A range that ends before today is left alone.
 *
 * @param {object|null|undefined} equityData
 * @param {number|null|undefined} liveEquity
 * @param {{ today?: string, rangeUntil?: string|null }} [opts]
 */
export function applyLiveBalanceToEquityCurve(equityData, liveEquity, opts = {}) {
  const equity = Number(liveEquity);
  if (!equityData || !Number.isFinite(equity) || equity <= 0) return equityData;

  const curve = Array.isArray(equityData.equityCurve) ? equityData.equityCurve : [];
  if (!curve.length) return equityData;

  const today = opts.today || tradingDateKey();
  if (opts.rangeUntil && opts.rangeUntil < today) return equityData;

  const last = curve[curve.length - 1];
  const lastTime = dateKey(last?.time);
  if (!lastTime || lastTime > today) return equityData;

  const rounded = roundMoney(equity);
  if (lastTime === today && sameMoney(last.equity, rounded)) return equityData;

  let nextCurve;
  if (lastTime === today) {
    nextCurve = curve.map((point, index) => (
      index === curve.length - 1
        ? { ...point, equity: rounded, pnlPct: undefined }
        : point
    ));
  } else {
    const carriedBenchmark = typeof last.benchmarkPct === 'number' ? last.benchmarkPct : null;
    nextCurve = [
      ...curve,
      {
        time: today,
        equity: rounded,
        pnlPct: undefined,
        benchmarkPct: carriedBenchmark,
      },
    ];
  }

  const baseline = nextCurve.find((point) => Number(point.equity) > 0)?.equity ?? null;
  const endEquity = Number(nextCurve[nextCurve.length - 1].equity);
  const accountPnlPct = baseline > 0
    ? pctFromBaseline(endEquity, baseline)
    : equityData.accountPnlPct;

  return {
    ...equityData,
    equityCurve: nextCurve,
    drawdownCurve: drawdownWithLiveEnd(nextCurve, equityData.drawdownCurve),
    rangeEnd: dateKey(nextCurve[nextCurve.length - 1].time),
    accountPnlPct,
  };
}
